import { useState } from 'react';
import { submitStationAnswer, completeStation } from '../../../services/rebusTeamService';

/**
 * Main station interaction screen showing questions and challenge.
 *
 * @param {Object} props
 * @param {Object} props.station - Station object from rebus data
 * @param {string} props.teamId - Current team ID
 * @param {string} props.sessionId - Current session ID
 * @param {Object} props.stationResults - Existing answers for this station
 * @param {Object} props.rebusData - Full rebus data (for answer checking)
 * @param {function} props.onComplete - Callback when station is completed
 */
export default function StationView({ station, teamId, sessionId, stationResults, rebusData, onComplete }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [submitResults, setSubmitResults] = useState({});
  const [errors, setErrors] = useState({});
  const [completing, setCompleting] = useState(false);

  if (!station) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <span className="material-symbols-outlined text-5xl mb-3">location_off</span>
        <p className="text-lg font-semibold">Ingen stasjon vald</p>
        <p className="text-sm">Vel ein stasjon frå lista</p>
      </div>
    );
  }

  const existingAnswers = stationResults?.answers || {};
  const isStationCompleted = !!stationResults?.completedAt;
  const questions = station.questions || [];
  const challenge = station.challenge || null;

  const handleInputChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setErrors((prev) => ({ ...prev, [questionId]: '' }));
  };

  const handleSubmitAnswer = async (questionId) => {
    const answer = answers[questionId];
    if (!answer || (typeof answer === 'string' && !answer.trim())) {
      setErrors((prev) => ({ ...prev, [questionId]: 'Skriv inn eit svar' }));
      return;
    }

    setSubmitting((prev) => ({ ...prev, [questionId]: true }));
    setErrors((prev) => ({ ...prev, [questionId]: '' }));

    try {
      const result = await submitStationAnswer(
        sessionId,
        teamId,
        station.id,
        questionId,
        typeof answer === 'string' ? answer.trim() : answer,
        rebusData
      );
      setSubmitResults((prev) => ({ ...prev, [questionId]: result }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [questionId]: err.message?.includes('allereie sendt')
          ? 'Du har allereie svart på dette spørsmålet'
          : err.message?.includes('ikkje starta')
          ? 'Mikkelchase er ikkje starta enno. Vent på at admin startar.'
          : err.message?.includes('avslutta')
          ? 'Mikkelchase er avslutta. Du kan ikkje sende inn fleire svar.'
          : 'Klarte ikkje sende svar. Prøv igjen.'
      }));
    } finally {
      setSubmitting((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleCompleteStation = async () => {
    setCompleting(true);
    try {
      await completeStation(sessionId, teamId, station.id);
      onComplete?.(station.id);
    } catch {
      // Silently handle — the station may already be completed
    } finally {
      setCompleting(false);
    }
  };

  const isQuestionAnswered = (questionId) => {
    const existing = existingAnswers[questionId];
    // If answer was rejected, treat as unanswered so user can resubmit
    if (existing && existing.approved === false) return false;
    return !!existing || !!submitResults[questionId];
  };

  const getAnswerResult = (questionId) => {
    const existing = existingAnswers[questionId];
    // If rejected, don't return result so the input form shows
    if (existing && existing.approved === false) return null;
    return existing || submitResults[questionId] || null;
  };

  const isQuestionRejected = (questionId) => {
    return existingAnswers[questionId]?.approved === false;
  };

  return (
    <div className="pb-32">
      {/* Station header */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-5 py-5 rounded-2xl mb-5 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center font-extrabold text-lg shrink-0">
            {station.order || '?'}
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-extrabold truncate">{station.name || 'Stasjon'}</h2>
            {station.description && (
              <p className="text-white/80 text-sm mt-0.5 line-clamp-2">{station.description}</p>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm text-white/70">
          <span className="material-symbols-outlined text-base">help_outline</span>
          <span>{questions.length} spørsmål</span>
          {challenge && (
            <>
              <span className="mx-1">&bull;</span>
              <span className="material-symbols-outlined text-base">emoji_events</span>
              <span>1 utfordring</span>
            </>
          )}
        </div>
      </div>

      {/* Questions list */}
      <div className="space-y-4 mb-6">
        {questions.map((question, index) => {
          const answered = isQuestionAnswered(question.id);
          const result = getAnswerResult(question.id);
          const isSubmitting = submitting[question.id];
          const questionError = errors[question.id];

          return (
            <div
              key={question.id}
              className={`bg-white rounded-2xl shadow-ambient overflow-hidden transition-colors ${
                answered ? 'ring-2 ring-emerald-300 bg-emerald-50/50' : ''
              }`}
            >
              {/* Question header */}
              <div className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 shrink-0">
                    {answered ? (
                      <span className="material-symbols-outlined text-2xl text-emerald-500">check_circle</span>
                    ) : (
                      <span className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-[15px] leading-snug">
                      {question.text}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                      <span className="material-symbols-outlined text-sm">star</span>
                      {question.points || 1} poeng
                    </span>
                  </div>
                </div>
              </div>

              {/* Answer section */}
              {!answered ? (
                <div className="px-5 pb-4">
                  {isQuestionRejected(question.id) && (
                    <div className="mb-3 space-y-2">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-700 text-sm font-semibold">
                        <span className="material-symbols-outlined text-lg">close</span>
                        <span>Svaret blei avvist — prøv igjen med nytt svar</span>
                      </div>
                      {existingAnswers[question.id]?.adminFeedback && (
                        <div className="border-l-4 border-amber-400 bg-amber-50 rounded-r-xl px-4 py-3">
                          <p className="text-xs text-amber-600 font-semibold mb-0.5">Tilbakemelding frå admin</p>
                          <p className="text-sm text-gray-800 italic">{existingAnswers[question.id].adminFeedback}</p>
                        </div>
                      )}
                      {existingAnswers[question.id]?.answer && (
                        <p className="text-xs text-gray-500 italic px-1">
                          Ditt førre svar: <span className="font-semibold text-gray-700">{existingAnswers[question.id].answer}</span>
                        </p>
                      )}
                    </div>
                  )}
                  {question.type === 'multiple_choice' ? (
                    /* Multiple choice */
                    <div className="space-y-2">
                      {(question.answers || []).map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => handleInputChange(question.id, option.id)}
                          disabled={isSubmitting}
                          className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-all active:scale-[0.98] ${
                            answers[question.id] === option.id
                              ? 'ring-2 ring-teal-500 bg-teal-50 text-teal-800 shadow-ambient'
                              : 'bg-gray-50 text-gray-700 shadow-ambient hover:shadow-lg'
                          }`}
                        >
                          {option.text}
                        </button>
                      ))}
                    </div>
                  ) : (
                    /* Text input */
                    <input
                      type="text"
                      value={answers[question.id] || ''}
                      onChange={(e) => handleInputChange(question.id, e.target.value)}
                      placeholder="Skriv svaret ditt her..."
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 rounded-xl ghost-border ghost-border-focus bg-gray-50 text-gray-900 placeholder:text-gray-400 font-medium transition-all"
                    />
                  )}

                  {questionError && (
                    <p className="text-red-600 text-sm font-medium mt-2">{questionError}</p>
                  )}

                  <button
                    type="button"
                    onClick={() => handleSubmitAnswer(question.id)}
                    disabled={isSubmitting || !answers[question.id]}
                    className="mt-3 w-full h-12 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 text-white font-bold rounded-xl shadow-ambient active:scale-[0.97] transition-all disabled:opacity-40 disabled:active:scale-100 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
                        <span>Sender...</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-xl">send</span>
                        <span>Send svar</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                /* Already answered feedback */
                <div className="px-5 pb-4 space-y-2">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold ${
                    result?.isCorrect === true || result?.approved === true
                      ? 'bg-emerald-100 text-emerald-700'
                      : result?.isCorrect === false || result?.approved === false
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <span className="material-symbols-outlined text-lg">
                      {result?.isCorrect === true || result?.approved === true
                        ? 'check'
                        : result?.isCorrect === false || result?.approved === false
                        ? 'close'
                        : 'schedule'}
                    </span>
                    <span>
                      {result?.approved === true
                        ? `Godkjent! +${result.points || 0} poeng`
                        : result?.isCorrect === true
                        ? `Rett! +${result.points || result.pointsEarned || 0} poeng`
                        : result?.approved === false || result?.isCorrect === false
                        ? 'Ikkje godkjent'
                        : 'Svar sendt — ventar på vurdering'}
                    </span>
                  </div>
                  {result?.adminFeedback && (
                    <div className="border-l-4 border-amber-400 bg-amber-50 rounded-r-xl px-4 py-3">
                      <p className="text-xs text-amber-600 font-semibold mb-0.5">Tilbakemelding frå admin</p>
                      <p className="text-sm text-gray-800 italic">{result.adminFeedback}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Challenge section */}
      {challenge && (
        <div className="bg-amber-50 shadow-ambient rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined text-2xl text-amber-600">emoji_events</span>
            <h3 className="font-extrabold text-lg text-amber-900">Utfordring</h3>
            <span className="ml-auto text-xs font-bold text-amber-600 bg-amber-100 px-2.5 py-1 rounded-full">
              Maks {challenge.maxPoints || 5} poeng
            </span>
          </div>
          <h4 className="font-bold text-amber-800 mb-1">{challenge.title}</h4>
          <p className="text-amber-700 text-sm leading-relaxed">{challenge.description}</p>
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-600 bg-amber-100/60 px-3 py-2 rounded-xl">
            <span className="material-symbols-outlined text-base">info</span>
            <span>Stasjonsleiar vil vurdere utfordringa</span>
          </div>
        </div>
      )}

      {/* Complete station button */}
      {!isStationCompleted ? (
        <button
          type="button"
          onClick={handleCompleteStation}
          disabled={completing}
          className="w-full h-14 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 text-white text-lg font-extrabold rounded-2xl shadow-lg active:scale-[0.97] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {completing ? (
            <>
              <span className="material-symbols-outlined animate-spin text-xl">progress_activity</span>
              <span>Fullfører...</span>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-xl">done_all</span>
              <span>Ferdig med stasjonen</span>
            </>
          )}
        </button>
      ) : (
        <div className="w-full flex items-center justify-center gap-2 h-14 bg-emerald-100 text-emerald-700 font-bold rounded-2xl">
          <span className="material-symbols-outlined text-xl">verified</span>
          <span>Stasjon fullført</span>
        </div>
      )}
    </div>
  );
}
